const nonEmptyString = (value) => (typeof value === 'string' && value.trim() ? value.trim() : null);

// GlobalExceptionHandlerMiddleware: { statusCode, message } where message is the raw exception text.
const isGlobalErrorBody = (body) => typeof body?.statusCode === 'number';

// VP plan limit: BadRequest/403 with { status: 403, message, upgradeMessage, vpLimit? }.
const isPlanLimitBody = (body) => body?.status === 403 && nonEmptyString(body?.upgradeMessage) !== null;

const planLimitMessage = (body) => {
  const upgrade = nonEmptyString(body.upgradeMessage);
  const message = nonEmptyString(body.message);
  if (!message) {
    return `You've reached your plan's VP limit. ${upgrade}`;
  }
  return message.includes(upgrade) ? message : `${message} ${upgrade}`;
};

// ASP.NET ValidationProblemDetails: { title, status, errors: { [field]: string[] } }.
// Keys look like "Name", "$.validUntil" or "Configurations[0].ShortDescription".
const fieldLabel = (key) => {
  const lastSegment = key.replace(/^\$\.?/, '').split('.').pop().replace(/\[\d+\]/g, '');
  const words = lastSegment
    .replace(/([a-z\d])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

const asSentence = (text) => (/[.!?]$/.test(text) ? text : `${text}.`);

const readableFieldError = (text) =>
  /^The JSON value could not be converted/i.test(text) ? "This value isn't valid." : asSentence(text);

const validationMessage = (errors) => {
  const parts = Object.entries(errors)
    .map(([key, messages]) => {
      const texts = (Array.isArray(messages) ? messages : [messages])
        .map(nonEmptyString)
        .filter(Boolean)
        .map(readableFieldError);
      if (!texts.length) {
        return null;
      }
      const label = fieldLabel(key);
      return label ? `${label}: ${texts.join(' ')}` : texts.join(' ');
    })
    .filter(Boolean);
  return parts.length ? `Please check these fields. ${parts.join(' ')}` : null;
};

const isValidationBody = (body) =>
  body?.errors != null && typeof body.errors === 'object' && !Array.isArray(body.errors);

// Bodies can arrive as unparsed text (getResponseObject keeps non-JSON text as a string).
const readBody = (raw) => {
  if (typeof raw !== 'string') {
    return raw;
  }
  const text = raw.trim();
  if (/^[{[]/.test(text)) {
    try {
      return JSON.parse(text);
    } catch {
      return null;
    }
  }
  return text;
};

const looksLikeMarkup = (text) => text.startsWith('<');

/**
 * Turn a failed API response into one sentence the user can read.
 *
 * @param {{ status?: number, body?: any, data?: any } | undefined} failed
 *   The failed response with its body already parsed (await `response.data` from fetchJSON first).
 *   `body` and `data` are aliases; either may be an object, a string, or empty.
 * @param {string} fallback What to show when the body says nothing useful, naming the action that
 *   failed, e.g. "We couldn't save this value proposition."
 * @returns {string}
 */
export const getApiErrorMessage = (failed, fallback) => {
  const body = readBody(failed?.body ?? failed?.data);
  if (typeof body === 'string') {
    return body && !looksLikeMarkup(body) ? body : fallback;
  }
  if (isPlanLimitBody(body)) {
    return planLimitMessage(body);
  }
  if (isValidationBody(body)) {
    return validationMessage(body.errors) || fallback;
  }
  if (isGlobalErrorBody(body) && body.statusCode >= 500) {
    return fallback;
  }
  // Some controllers return an ASP.NET-style { Message } instead of { message }.
  return nonEmptyString(body?.message) || nonEmptyString(body?.Message) || fallback;
};
